import {
  Card,
  Descriptions,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Typography,
  Select,
  Alert,
  Tag,
} from "antd";
import React, { useState, useEffect } from "react";
import { get, post } from "../../utils/http";
import { PERMISSIONS } from "../../utils/consts";

const { Text } = Typography;

interface ConfigItem {
  config_key: string;
  config_value: string;
  label: string;
}

interface RecommendationScene {
  id: string;
  scene_name: string;
  scene_name_en: string;
  module_name: string;
  permission_type: PERMISSIONS;
}

interface DailyRecommendation {
  date: string;
  source: "auto" | "manual";
  scene_ids: string[];
  candidates: RecommendationScene[];
}

const configList: ConfigItem[] = [
  {
    config_key: "image_prompt",
    config_value: "",
    label: "图片生成AI prompt",
  },
  {
    config_key: "dialog_prompt",
    config_value: "",
    label: "对话生成AI prompt",
  },
  {
    config_key: "points_register_gift",
    config_value: "100",
    label: "新用户注册赠送积分",
  },
  {
    config_key: "points_unlock_scene",
    config_value: "-10",
    label: "解锁场景扣除积分",
  },
  {
    config_key: "points_finish_scene",
    config_value: "20",
    label: "完成场景奖励积分",
  },
  {
    config_key: "points_review_scene",
    config_value: "5",
    label: "复习场景奖励积分",
  },
  {
    config_key: "points_daily_checkin",
    config_value: "10",
    label: "每日签到奖励积分",
  },
  {
    config_key: "points_share",
    config_value: "15",
    label: "分享/成功邀请奖励(邀请人)",
  },
  {
    config_key: "points_invitee_gift",
    config_value: "20",
    label: "接受邀请新人礼包(被邀请人)",
  },
];

function SystemConfig() {
  const [configs, setConfigs] = useState<ConfigItem[]>(configList);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ConfigItem | null>(null);
  const [dailyRecommendation, setDailyRecommendation] =
    useState<DailyRecommendation | null>(null);
  const [selectedRecommendationIds, setSelectedRecommendationIds] = useState<
    string[]
  >([]);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [recommendationSaving, setRecommendationSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchConfigs();
    fetchDailyRecommendations();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await get("/api/manage/configs");
      if (response?.data?.code === 0) {
        const data = response.data.data || [];
        const updatedConfigs = configList.map((config) => {
          const found = data.find(
            (item: any) => item.config_key === config.config_key
          );
          return {
            ...config,
            config_value: found ? found.config_value : "",
          };
        });
        setConfigs(updatedConfigs);
      }
    } catch (error) {
      console.error("🚀 ~ fetchConfigs ~ error:", error);
    }
  };

  const handleEdit = (config: ConfigItem) => {
    setEditingConfig(config);
    form.setFieldsValue({ config_value: config.config_value });
    setModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingConfig(null);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      await post("/api/manage/config-save", {
        config_key: editingConfig?.config_key,
        config_value: values.config_value,
      });
      message.success("保存成功");
      setModalVisible(false);
      setEditingConfig(null);
      form.resetFields();
      fetchConfigs();
    } catch (error) {
      console.error("🚀 ~ handleSave ~ error:", error);
    }
  };

  const fetchDailyRecommendations = async () => {
    setRecommendationLoading(true);
    try {
      const response = await get("/api/manage/daily-recommendations");
      if (response?.data?.code === 0) {
        const data = response.data.data as DailyRecommendation;
        setDailyRecommendation(data);
        setSelectedRecommendationIds(data.scene_ids || []);
      }
    } catch (error) {
      console.error("获取今日推荐失败:", error);
    } finally {
      setRecommendationLoading(false);
    }
  };

  const handleDailyRecommendationSave = async () => {
    const eligibleIds = new Set(
      (dailyRecommendation?.candidates || [])
        .filter((scene) => scene.permission_type === PERMISSIONS.FREE)
        .map((scene) => scene.id)
    );
    if (
      selectedRecommendationIds.length !== 3 ||
      selectedRecommendationIds.some((sceneId) => !eligibleIds.has(sceneId))
    ) {
      message.warning("请选择3个已上架、已勾选推荐的免费场景");
      return;
    }

    setRecommendationSaving(true);
    try {
      const response = await post("/api/manage/daily-recommendations", {
        scene_ids: selectedRecommendationIds,
      });
      if (response?.data?.code === 0) {
        const data = response.data.data as DailyRecommendation;
        setDailyRecommendation(data);
        setSelectedRecommendationIds(data.scene_ids);
        message.success("今日推荐已更新");
      }
    } catch (error) {
      console.error("保存今日推荐失败:", error);
    } finally {
      setRecommendationSaving(false);
    }
  };

  const eligibleRecommendationCandidates = (
    dailyRecommendation?.candidates || []
  ).filter((scene) => scene.permission_type === PERMISSIONS.FREE);
  const eligibleRecommendationIds = new Set(
    eligibleRecommendationCandidates.map((scene) => scene.id)
  );
  const canSaveDailyRecommendation =
    selectedRecommendationIds.length === 3 &&
    selectedRecommendationIds.every((sceneId) =>
      eligibleRecommendationIds.has(sceneId)
    );

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="今日推荐课程"
        loading={recommendationLoading}
        style={{ marginBottom: 24 }}
        extra={
          dailyRecommendation && (
            <Space>
              <span>{dailyRecommendation.date}</span>
              <Tag color={dailyRecommendation.source === "manual" ? "blue" : "green"}>
                {dailyRecommendation.source === "manual"
                  ? "管理员设置"
                  : "系统随机"}
              </Tag>
            </Space>
          )
        }
      >
        {dailyRecommendation &&
          eligibleRecommendationCandidates.length < 3 && (
            <Alert
              type="warning"
              showIcon
              message="当前已上架、已勾选推荐且免费的场景不足3个，请先在场景管理中补充。"
              style={{ marginBottom: 16 }}
            />
          )}
        <Space.Compact style={{ width: "100%" }}>
          <Select
            mode="multiple"
            maxCount={3}
            value={selectedRecommendationIds}
            onChange={setSelectedRecommendationIds}
            placeholder="请选择3个免费场景作为今日推荐"
            style={{ width: "100%" }}
            optionFilterProp="label"
            options={eligibleRecommendationCandidates.map((scene) => ({
              value: scene.id,
              label: `${scene.scene_name} / ${scene.scene_name_en} · ${scene.module_name} · 免费`,
            }))}
          />
          <Button
            type="primary"
            loading={recommendationSaving}
            disabled={!canSaveDailyRecommendation}
            onClick={handleDailyRecommendationSave}
          >
            保存今日推荐
          </Button>
        </Space.Compact>
        <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
          系统按北京时间自然日生成推荐；当天首次访问会从已上架、已勾选推荐且免费的场景中随机选取3个。手动保存后，当天所有用户将看到此处设置的顺序。
        </Text>
      </Card>

      <Card title="系统配置">
        <div style={{ marginBottom: 16 }}>
          <Text
            strong
            style={{ fontSize: 16, display: "block", marginBottom: 8 }}
          >
            AI配置
          </Text>
        </div>
        <Descriptions column={1} bordered>
          {configs.map((config) => (
            <Descriptions.Item
              key={config.config_key}
              label={config.label}
              labelStyle={{ width: 200 }}
            >
              <Space>
                <span style={{ flex: 1 }}>{config.config_value || "-"}</span>
                <Button type="link" onClick={() => handleEdit(config)}>
                  修改
                </Button>
              </Space>
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Card>

      <Modal
        title={`修改${editingConfig?.label}`}
        open={modalVisible}
        onOk={handleSave}
        onCancel={handleCancel}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={editingConfig?.label}
            name="config_value"
            rules={[
              { required: true, message: `请输入${editingConfig?.label}` },
            ]}
          >
            <Input.TextArea
              rows={6}
              placeholder={`请输入${editingConfig?.label}`}
            />
          </Form.Item>
          <Form.Item>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {`可在prompt任意位置使用以下变量：@{name}@代表场景名称，@{desc}@代表场景描述，@{module}@代表所属模块`}
            </Text>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default SystemConfig;
